/**
 * Default Organization Utilities
 * 
 * Utilities for managing default organization in localStorage
 */

// Local storage key for default organization
export const DEFAULT_ORG_KEY = 'ncoder_default_organization_id';

/**
 * Get the default organization ID from localStorage
 * @returns string | null - The default organization ID or null if not set
 */
export function getDefaultOrgId(): string | null {
  try {
    return localStorage.getItem(DEFAULT_ORG_KEY);
  } catch (error) {
    console.error('Failed to get default organization from localStorage:', error);
    return null;
  }
}

/**
 * Set the default organization ID in localStorage
 * @param orgId - The organization ID to set as default
 */
export function setDefaultOrgId(orgId: string): void {
  try {
    localStorage.setItem(DEFAULT_ORG_KEY, orgId);
    document.cookie = `org_id=${encodeURIComponent(orgId)}; path=/; samesite=lax; max-age=${60 * 60 * 24 * 30}`;
  } catch (error) {
    console.error('Failed to set default organization in localStorage:', error);
  }
}

/**
 * Check if an organization is the default one
 * @param orgId - The organization ID to check
 * @returns boolean - True if the organization is the default one
 */
export function isDefaultOrg(orgId: string): boolean {
  const defaultOrgId = getDefaultOrgId();
  return defaultOrgId === orgId;
}

/**
 * Clear the default organization from localStorage
 */
export function clearDefaultOrg(): void {
  try {
    localStorage.removeItem(DEFAULT_ORG_KEY);
    document.cookie = 'org_id=; path=/; max-age=0; samesite=lax';
    console.log('Cleared default organization');
  } catch (error) {
    console.error('Failed to clear default organization from localStorage:', error);
  }
}

/**
 * Ensure a default organization is set
 * If no default is set and availableOrgIds are provided, set the first one as default
 * @param availableOrgIds - Array of available organization IDs
 * @returns string | null - The default organization ID
 */
export function ensureDefaultOrg(availableOrgIds: string[]): string | null {
  let defaultOrgId = getDefaultOrgId();
  
  if (!defaultOrgId && availableOrgIds.length > 0) {
    // No default set, use first available org
    const firstOrgId = availableOrgIds[0];
    setDefaultOrgId(firstOrgId);
    defaultOrgId = firstOrgId;
  } else if (defaultOrgId && !availableOrgIds.includes(defaultOrgId)) {
    // Default org no longer exists, use first available org
    if (availableOrgIds.length > 0) {
      const firstOrgId = availableOrgIds[0];
      setDefaultOrgId(firstOrgId);
      defaultOrgId = firstOrgId;
    } else {
      clearDefaultOrg();
      defaultOrgId = null;
    }
  }
  
  return defaultOrgId;
}

/**
 * Organization default management hook
 * Returns utilities for managing default organization
 */
export function useDefaultOrg() {
  return {
    getDefaultOrgId,
    setDefaultOrgId,
    isDefaultOrg,
    clearDefaultOrg,
    ensureDefaultOrg,
  };
}
